/**
 * Trigger payload fields are spread onto this context; `full-pdp`, for example, adds images,
 * media, pid, handle, and tags.
 * @typedef {Object} RegistryContext
 * @property {string} trigger
 * @property {Element[]} roots
 */

/**
 * @callback RegistryCondition
 * @param {Element} element
 * @param {RegistryContext} context
 * @returns {boolean|undefined}
 */

/**
 * @callback RegistryBeforeMount
 * @param {Element} element
 * @param {RegistryContext} context
 * @returns {void}
 */

/**
 * @callback RegistryGetProps
 * @param {Element} element
 * @param {RegistryContext} context
 * @returns {Object|undefined}
 */

/**
 * Synchronous custom mounts with `element.__vue_app__` are auto-tracked. Delayed or external
 * mounts must call `track(element)` after mounting; registry order decides overlapping selectors.
 * @callback RegistryMount
 * @param {Element} element
 * @param {RegistryContext} context
 * @returns {boolean|undefined}
 */

/**
 * @typedef {Object} RegistryEntry
 * @property {string} key
 * @property {string} selector
 * @property {string[]} triggers
 * @property {import('vue').Component} [component]
 * @property {RegistryMount} [mount]
 * @property {boolean} [multiple]
 * @property {RegistryCondition} [shouldMount]
 * @property {RegistryBeforeMount} [beforeMount]
 * @property {RegistryGetProps} [getProps]
 */

const failRegistry = message => {
  const error = new Error(`Invalid Vue component registry: ${message}`);
  if (import.meta.env.DEV) throw error;
  console.error(error);
};

const validateRegistry = registry => {
  if (!Array.isArray(registry)) {
    failRegistry('registry must be an array');
    return [];
  }

  const keys = new Set();
  const validEntries = [];
  registry.forEach((entry, index) => {
    const label = entry?.key || `entry ${index}`;
    let message = '';

    if (!entry?.key) message = `${label} is missing a key`;
    else if (keys.has(entry.key)) message = `duplicate registry key: ${entry.key}`;
    else if (typeof entry.selector !== 'string' || !entry.selector.trim()) message = `${label} is missing a selector`;
    else if (!Array.isArray(entry.triggers) || !entry.triggers.length) message = `${label} is missing triggers`;
    else if (!entry.component && !entry.mount) message = `${label} is missing a component or custom mount strategy`;
    else if (entry.component && entry.mount) message = `${label} cannot define both a component and custom mount strategy`;

    if (!message) {
      try {
        document.createDocumentFragment().querySelector(entry.selector);
      } catch (error) {
        message = `${label} has an invalid selector "${entry.selector}": ${error.message}`;
      }
    }

    if (message) {
      failRegistry(message);
      return;
    }

    keys.add(entry.key);
    validEntries.push(entry);
  });
  return validEntries;
};

/**
 * Builds the trigger-scoped mount lifecycle for a Vue island registry. Invalid entries are dropped
 * (and throw in dev builds) before any trigger runs.
 * @param {Object} options
 * @param {Function} options.createApp Vue `createApp`, or a wrapper with the same signature.
 * @param {RegistryEntry[]} options.registry
 * @returns {{initialize: function((Document|Element), string, Object=): Element[], teardown: function(Element): Element[], track: function(Element): void}}
 *   `initialize` mounts `root` and its descendants for `trigger`, spreading `context` onto the
 *   registry context; `teardown` unmounts tracked apps inside `root`; `track` records a custom mount.
 */
export const createVueComponentInitializer = ({ createApp, registry }) => {
  const validRegistry = validateRegistry(registry);
  const entriesByTrigger = new Map();
  const selectorsByTrigger = new Map();
  const mountedElements = new Set();

  const getEntries = trigger => {
    if (!entriesByTrigger.has(trigger))
      entriesByTrigger.set(
        trigger,
        validRegistry.filter(entry => entry.triggers.includes(trigger))
      );
    return entriesByTrigger.get(trigger);
  };

  const queryRoots = (root, entries, trigger) => {
    if (!root || !entries.length) return [];

    if (!selectorsByTrigger.has(trigger)) selectorsByTrigger.set(trigger, [...new Set(entries.map(entry => entry.selector))].join(', '));
    const selector = selectorsByTrigger.get(trigger);
    try {
      const roots = root.matches?.(selector) ? [root] : [];
      return [...new Set([...roots, ...root.querySelectorAll(selector)])];
    } catch (error) {
      const registryError = new Error(`Vue component registry query failed for trigger "${trigger}": ${error.message}`);
      registryError.cause = error;
      throw registryError;
    }
  };

  const mountElement = (element, entry, context) => {
    if (!element || element.__vue_app__) return false;
    if (entry.shouldMount?.(element, context) === false) return false;

    try {
      if (entry.mount) {
        const mounted = entry.mount(element, context) !== false;
        if (mounted && element.__vue_app__) mountedElements.add(element);
        return mounted;
      }

      entry.beforeMount?.(element, context);
      const props = entry.getProps?.(element, context);
      const app = props === undefined ? createApp(entry.component) : createApp(entry.component, props);
      app.mount(element);
      mountedElements.add(element);
      return true;
    } catch (error) {
      const registryError = new Error(`Vue component registry mount failed for "${entry.key}": ${error.message}`);
      registryError.cause = error;
      throw registryError;
    }
  };

  const initialize = (root, trigger, context = {}) => {
    const entries = getEntries(trigger);
    let roots;
    try {
      roots = queryRoots(root, entries, trigger);
    } catch (error) {
      console.error(error);
      return [];
    }
    if (!roots.length) return [];

    const rootsByEntry = new Map(entries.map(entry => [entry, []]));
    roots.forEach(element => {
      const matchingEntry = entries.find(entry => element.matches(entry.selector));
      if (import.meta.env.DEV) {
        const matches = entries.filter(entry => element.matches(entry.selector));
        if (matches.length > 1) console.error(`Vue component root matches multiple registry entries: ${matches.map(entry => entry.key).join(', ')}`);
      }
      if (matchingEntry) rootsByEntry.get(matchingEntry).push(element);
    });

    const runContext = { ...context, trigger, roots };
    const initialized = [];
    entries.forEach(entry => {
      const matchingRoots = rootsByEntry.get(entry);
      const candidates = entry.multiple ? matchingRoots : matchingRoots.slice(0, 1);
      candidates.forEach(element => {
        try {
          if (mountElement(element, entry, runContext)) initialized.push(element);
        } catch (error) {
          console.error(error);
        }
      });
    });
    return initialized;
  };

  const track = element => {
    if (element?.__vue_app__) mountedElements.add(element);
  };

  const teardown = root => {
    if (!root) return [];

    const unmounted = [];
    mountedElements.forEach(element => {
      if (element !== root && !root.contains?.(element)) return;

      element.__vue_app__?.unmount();
      mountedElements.delete(element);
      unmounted.push(element);
    });
    return unmounted;
  };

  return { initialize, teardown, track };
};
